/* eslint-disable camelcase */
import { FC, useState, useEffect, useRef, RefObject, useCallback } from 'react';
import { useSelector } from 'react-redux';
import html2canvas from 'html2canvas';
import { selectIsCxUniversity, selectTenantName, selectUserUuid } from '@redux/slices/userSlice';
import { useGetCourseCertificate } from '@queries/platform/course';
import CertificatePopup from './certificatePopup';
import { useCertificateShare } from '../hooks/useCertificateShare';

const CertificatePopupContainer: FC<CertificatePopupContainerProps> = (props: CertificatePopupContainerProps) => {
    const { onClose, courseUuid, isCustom, inputCertificateData, isSkillTree, isLearningPath } = props;
    const userUuid = useSelector(selectUserUuid);
    const isCxUniversity = useSelector(selectIsCxUniversity);
    const tenantName = useSelector(selectTenantName);
    const [certificateData, setCertificateData] = useState<any>({});
    const printRef: RefObject<HTMLDivElement> = useRef(null);

    const { data, isLoading: isCertificateDataLoading } = useGetCourseCertificate(courseUuid || '', userUuid);

    const { onUploadCertificateToLinkedin, isCertUploading, setIsCertUploading, showError } = useCertificateShare();

    useEffect(() => {
        if (!data?.certificate_preview_data) return;
        const { email_or_name, course_display_name, end_date, completed_at } = data.certificate_preview_data;
        setCertificateData({
            name: email_or_name,
            entityDisplayName: course_display_name,
            endDate: end_date,
            completedAt: completed_at || ''
        });
    }, [data]);

    useEffect(() => {
        if (inputCertificateData) {
            setCertificateData(inputCertificateData);
        }
    }, []);

    const createAnImage = async (printRef: React.RefObject<HTMLDivElement>): Promise<string | undefined> => {
        if (printRef.current) {
            const canvas = await html2canvas(printRef.current, { scale: 5 });
            const dataUrl = canvas.toDataURL('image/png');
            return dataUrl;
        }
        return undefined;
    };

    const handleLinkedinShare = async () => {
        try {
            setIsCertUploading(true);
            const imageDataUri = await createAnImage(printRef);
            if (imageDataUri) {
                onUploadCertificateToLinkedin(imageDataUri);
            }
        } catch (err) {
            showError((err as Error).message);
            setIsCertUploading(false);
        }
    };

    return (
        <CertificatePopup
            onClose={onClose}
            certificateData={certificateData}
            isCertificateDataLoading={isCertificateDataLoading}
            handleLinkedinShare={handleLinkedinShare}
            refCert={printRef}
            isCertUploading={isCertUploading}
            isCxUniversity={isCxUniversity}
            isOwasp={tenantName.toLowerCase() === 'owasp'}
            isBP={tenantName.toLowerCase() === 'bp'}
            isCustom={isCustom}
            isSkillTree={isSkillTree}
            isLearningPath={isLearningPath}
        />
    );
};

interface CertificatePopupContainerProps {
    onClose: Function;
    isSkillTree: boolean;
    isLearningPath: boolean;
    setIsCustomCourse?: Function;
    courseUuid?: string;
    isCustom: boolean;
    inputCertificateData?: {
        name: string;
        entityDisplayName: string;
        endDate?: string;
        completedAt?: string;
    };
}

export default CertificatePopupContainer;
